'use es6';

import { createSelector } from 'reselect';
import I18n from 'I18n';
import { getEmailConfig } from 'EmailEditorUI/redux/selectors/sharedSelectors';
import { uniq } from 'underscore';
import { getHasSubscriptionGroups, getHasSubscriptionGroupsWriteAccess, getIsUngatedForTranslationMissingWarning, getCanUseMarketingEmailAPI, getIsUngatedForBEValidation } from 'EmailEditorUI/redux/selectors/AuthSelectors';
import { createContentModelSelector } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import ContentRoutes from 'ContentUtils/Routes';
import { getLanguage, getEmailTypeRequiresSubscription } from 'EmailEditorUI/redux/selectors/EmailSelectors';
import { getIsABVariant } from 'ContentEditorUI/redux/selectors/abTestSelectors';
import { EditorMetricsTracker } from 'ContentEditorUI/utils/metricsTracker';
import { getFieldErrorMap } from 'EmailEditorUI/redux/selectors/ApiValidationSelectors';
import { hasAccessToBusinessUnit } from 'EmailEditorUI/redux/selectors/AuthSelectors';
import { getEmailBusinessUnitId, getIsSingleSendAPIEmail } from 'EmailEditorUI/redux/selectors/EmailSelectors';
import { SUBSCRIPTION_BU_MISMATCH_ERROR } from 'EmailEditorUI/utils/metricNames';
const getUserAccessibleSubscriptionGroups = createSelector(getEmailConfig, config => config.userAccessibleSubscriptionGroups);
export const getSubscriptionId = createContentModelSelector('subscription');
export const getPreferencesGroupId = createContentModelSelector('preferencesGroupId');
export const getIsTransactional = createContentModelSelector('transactional');
export const getScrubsSubscriptionLinks = createContentModelSelector('scrubsSubscriptionLinks');
export const getIsTransactionalEmail = createSelector([getSubscriptionId, getIsTransactional], (subscription, transactional) => parseInt(subscription, 10) === -1 && transactional);
export const getIsTransactionalSingleSendAPIEmail = createSelector([getIsTransactionalEmail, getIsSingleSendAPIEmail], (isTransactional, isSingleSend) => isTransactional && isSingleSend);
export const getIsMarketingSingleSendAPIEmail = createSelector([getIsTransactionalEmail, getIsSingleSendAPIEmail], (isTransactional, isSingleSend) => !isTransactional && isSingleSend);
export const getSubscriptionGroups = createSelector(getEmailConfig, config => (config === null || config === void 0 ? void 0 : config.subscriptionGroups) || []);
export const getPreferencesGroupName = createSelector([getSubscriptionGroups, getPreferencesGroupId], (groups, id) => {
  var _group$preferencesGro;
  if (!(groups !== null && groups !== void 0 && groups.length) || !id) {
    return '';
  }
  const group = groups.find(g => {
    var _g$preferencesGroup;
    return ((_g$preferencesGroup = g.preferencesGroup) === null || _g$preferencesGroup === void 0 ? void 0 : _g$preferencesGroup.id) === id;
  });
  return (group === null || group === void 0 || (_group$preferencesGro = group.preferencesGroup) === null || _group$preferencesGro === void 0 ? void 0 : _group$preferencesGro.name) || '';
});
export const getSubscriptionGroupsAsOptions = createSelector([getSubscriptionGroups, getUserAccessibleSubscriptionGroups, getPreferencesGroupId], (groups, accessibleGroups, selectedId) => {
  const optionIds = new Set(accessibleGroups).add(selectedId);
  return groups.filter(group => {
    var _group$preferencesGro2;
    return optionIds.has((_group$preferencesGro2 = group.preferencesGroup) === null || _group$preferencesGro2 === void 0 ? void 0 : _group$preferencesGro2.id);
  }).map(group => {
    var _group$preferencesGro3, _group$preferencesGro4;
    return {
      text: group === null || group === void 0 || (_group$preferencesGro3 = group.preferencesGroup) === null || _group$preferencesGro3 === void 0 ? void 0 : _group$preferencesGro3.name,
      value: group === null || group === void 0 || (_group$preferencesGro4 = group.preferencesGroup) === null || _group$preferencesGro4 === void 0 ? void 0 : _group$preferencesGro4.id
    };
  });
});
export const getHasSelectedSubscription = createSelector(getSubscriptionId, Boolean);
const groupSubscriptionTypesAsOptions = group => group.translatedSubscriptionDefinitions.map(definition => {
  const subscriptionDefinition = (definition === null || definition === void 0 ? void 0 : definition.subscriptionDefinition) || {};
  return {
    text: subscriptionDefinition === null || subscriptionDefinition === void 0 ? void 0 : subscriptionDefinition.name,
    value: subscriptionDefinition === null || subscriptionDefinition === void 0 ? void 0 : subscriptionDefinition.id,
    primaryLanguage: subscriptionDefinition === null || subscriptionDefinition === void 0 ? void 0 : subscriptionDefinition.primaryLanguage,
    languageMap: definition === null || definition === void 0 ? void 0 : definition.languageMap,
    isDefault: !!(subscriptionDefinition !== null && subscriptionDefinition !== void 0 && subscriptionDefinition.defaultSubscription)
  };
});
export const getSubscriptionDefinitionsOptions = createSelector([getEmailConfig, getSubscriptionGroups, getPreferencesGroupId, getHasSelectedSubscription, getHasSubscriptionGroups, hasAccessToBusinessUnit, getEmailBusinessUnitId], (emailConfig, groups, groupId, hasSelectedSubscription, hasSubscriptionGroups, canAccessToBusinessUnit, emailBusinessUnitId) => {
  const shouldFilterAccountBusinessUnit = canAccessToBusinessUnit && !emailBusinessUnitId;
  if (!hasSubscriptionGroups) {
    var _emailConfig$subscrip, _subscriptionList;
    let subscriptionList = emailConfig === null || emailConfig === void 0 || (_emailConfig$subscrip = emailConfig.subscriptionDefinitions) === null || _emailConfig$subscrip === void 0 ? void 0 : _emailConfig$subscrip.data;
    if (shouldFilterAccountBusinessUnit && subscriptionList) {
      subscriptionList = subscriptionList.filter(subscription => !subscription.subscriptionDefinition.businessUnitId);
    }
    // Tracking metric if subscription list does not contain BU related subscriptions
    if (canAccessToBusinessUnit && subscriptionList) {
      const hasBURelatedSubscriptions = subscriptionList.find(subscription => subscription.subscriptionDefinition.businessUnitId === emailBusinessUnitId);
      if (subscriptionList.length > 0 && !hasBURelatedSubscriptions) {
        EditorMetricsTracker.counter(SUBSCRIPTION_BU_MISMATCH_ERROR).increment();
      }
    }
    return ((_subscriptionList = subscriptionList) === null || _subscriptionList === void 0 ? void 0 : _subscriptionList.map(({
      subscriptionDefinition,
      languageMap
    }) => ({
      text: subscriptionDefinition.name,
      value: subscriptionDefinition.id,
      disabled: subscriptionDefinition.internal,
      primaryLanguage: subscriptionDefinition.primaryLanguage,
      languageMap
    }))) || [];
  }
  if (!groupId || groups.length === 0) {
    if (hasSelectedSubscription) {
      // could have a subscription but not group selected for e.g. old automated emails
      const allOptions = groups.flatMap(group => groupSubscriptionTypesAsOptions(group));
      return uniq(allOptions, false, o => o.value);
    }
    return [];
  }
  const group = groups.find(g => {
    var _g$preferencesGroup2;
    return ((_g$preferencesGroup2 = g.preferencesGroup) === null || _g$preferencesGroup2 === void 0 ? void 0 : _g$preferencesGroup2.id) === groupId;
  });
  if (!group) {
    return [];
  }
  return groupSubscriptionTypesAsOptions(group);
});
const createOptionSelector = optionFilter => {
  return createSelector([getSubscriptionDefinitionsOptions, getIsSingleSendAPIEmail, getCanUseMarketingEmailAPI], (options, isSingleSend, canUseMarketingAPI) => {
    const optionsToUse = options.filter(optionFilter);
    if (isSingleSend && !canUseMarketingAPI) {
      return optionsToUse.map(option => Object.assign({}, option, {
        disabled: true
      }));
    }
    return optionsToUse;
  });
};
export const getDefaultSubscriptionDefinitionsOptions = createOptionSelector(o => o.isDefault && !o.disabled);
export const getCustomSubscriptionDefinitionsOptions = createOptionSelector(o => !o.isDefault && !o.disabled);
export const getInternalSubscriptionDefinitionOptions = createOptionSelector(o => o.disabled);
export const getSubscriptionGroup = createSelector([getPreferencesGroupId, getSubscriptionGroups], (id, groups) => {
  return groups.find(group => {
    var _group$preferencesGro5;
    return ((_group$preferencesGro5 = group.preferencesGroup) === null || _group$preferencesGro5 === void 0 ? void 0 : _group$preferencesGro5.id) === id;
  });
});
export const getSubscriptionGroupName = createSelector(getSubscriptionGroup, group => {
  var _group$preferencesGro6;
  return (group === null || group === void 0 || (_group$preferencesGro6 = group.preferencesGroup) === null || _group$preferencesGro6 === void 0 ? void 0 : _group$preferencesGro6.name) || '';
});
export const getSubscriptionName = createSelector([getSubscriptionDefinitionsOptions, getSubscriptionId], (subscriptions, currentId) => {
  if (!(subscriptions !== null && subscriptions !== void 0 && subscriptions.length)) {
    return undefined;
  }
  const currentSubscription = subscriptions.find(sub => sub.value === currentId);
  return currentSubscription === null || currentSubscription === void 0 ? void 0 : currentSubscription.text;
});
export const getSubscriptionValue = createSelector([getSubscriptionId, getSubscriptionName, getIsTransactional], (subscription, name, transactional) => {
  return {
    subscription,
    name,
    transactional
  };
});
export const getSubscriptionValidationErrors = createSelector([getEmailTypeRequiresSubscription, getIsABVariant, getSubscriptionValue, getIsTransactional, getHasSubscriptionGroups, getLanguage, getSubscriptionDefinitionsOptions, getIsUngatedForTranslationMissingWarning, getFieldErrorMap, hasAccessToBusinessUnit, getIsUngatedForBEValidation], (emailTypeRequiresSubscription, isVariant, subscriptionValue, transactional, canUseSubcriptionGroups, selectedLanguage, subscriptionDefinitions, isUngatedForTranslationWarning, fieldErrors, canAccessBusinessUnit, isUngatedForBEValidation) => {
  if (!emailTypeRequiresSubscription || isVariant || transactional) {
    return {};
  }
  if (canAccessBusinessUnit && fieldErrors.SUBSCRIPTION && fieldErrors.SUBSCRIPTION.some(({
    errorKey
  }) => errorKey === 'SUBSCRIPTION_TYPE_BUSINESS_UNIT_VALIDATION')) {
    const res = {
      subscription: {
        pane: 'settings',
        target: 'subscription',
        message: I18n.text('panes.review.sections.settings.items.subscription.errors.businessUnitSubscriptionError_buv2'),
        type: 'required'
      }
    };
    return res;
  }
  if (isUngatedForBEValidation) {
    var _fieldErrors$SUBSCRIP;
    if ((_fieldErrors$SUBSCRIP = fieldErrors['SUBSCRIPTION']) !== null && _fieldErrors$SUBSCRIP !== void 0 && _fieldErrors$SUBSCRIP.some(e => e.errorKey === 'NOT_SET')) {
      return {
        subscription: {
          pane: 'settings',
          message: I18n.text('editors.subscription.missing'),
          type: 'required'
        }
      };
    }
  } else if (!subscriptionValue.name || subscriptionValue.name.length === 0) {
    return {
      subscription: {
        pane: 'settings',
        message: I18n.text('editors.subscription.missing'),
        type: 'required'
      }
    };
  }

  // logic potentially needs updating here
  if (isUngatedForTranslationWarning) {
    const selectedDefinition = subscriptionDefinitions.find(d => d.value === subscriptionValue.subscription);
    if (!selectedLanguage || selectedLanguage.includes('en') || !selectedDefinition || selectedLanguage === selectedDefinition.primaryLanguage || Object.prototype.hasOwnProperty.call(selectedDefinition.languageMap, selectedLanguage)) {
      return {};
    }
    return {
      subscription: {
        pane: 'settings',
        type: 'missing_subscription_type_translation',
        warning: true,
        showImmediately: true,
        data: {
          url: ContentRoutes.settings('email', canUseSubcriptionGroups ? 'preferences-center' : 'types'),
          subscriptionName: subscriptionValue.name,
          language: I18n.text(`SharedI18nStrings.languageNames.${selectedLanguage}`)
        }
      }
    };
  }
  return {};
});
export const getSubscriptionGroupValidationErrors = createSelector([getHasSubscriptionGroupsWriteAccess, getPreferencesGroupId, getEmailTypeRequiresSubscription], (hasGroups, preferencesGroupId, emailTypeRequiresSubscription) => {
  if (!hasGroups || !emailTypeRequiresSubscription || typeof preferencesGroupId === 'number') {
    return {};
  }
  return {
    preferencesGroup: {
      pane: 'settings',
      message: I18n.text('editors.subscription.groupMissing'),
      type: 'required'
    }
  };
});